import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Drawer,
  InputBase,
  IconButton,
  Chip,
  Fade,
} from '@mui/material';
import {
  FaSearch,
  FaRegLightbulb,
  FaCompass,
  FaBookOpen,
  FaTimes,
  FaArrowRight,
  FaCheck,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import { IoCheckmarkCircleOutline } from 'react-icons/io5';
import { navigate } from 'gatsby';
import {
  searchNavigator,
  POPULAR_CATEGORIES,
  GOAL_PROMPTS,
} from './guideSearchData';

const OPTIONS = [
  {
    id: 'find',
    title: 'I know what I need',
    desc: 'Find a particular tool, service or resource',
    icon: <FaSearch size={18} />,
  },
  {
    id: 'solve',
    title: 'I need help solving a problem',
    desc: 'Get suggestions based on your goal',
    icon: <FaRegLightbulb size={18} />,
  },
  {
    id: 'explore',
    title: "I'm just exploring",
    desc: 'Discover useful tools & innovations',
    icon: <FaCompass size={18} />,
  },
  {
    id: 'learn',
    title: 'I want to learn something',
    desc: 'Explore guides, research & benchmarks',
    icon: <FaBookOpen size={18} />,
  },
];

const SmartGuidePanel = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState('find');
  const [inputValue, setInputValue] = useState('');

  // Lock body scroll or add active class and suppress chat widgets when drawer is open
  useEffect(() => {
    if (typeof document !== 'undefined') {
      if (open) {
        document.body.classList.add('guide-active');
        if (typeof window !== 'undefined' && window.Tawk_API?.hideWidget) {
          try { window.Tawk_API.hideWidget(); } catch (e) {}
        }
      } else {
        document.body.classList.remove('guide-active');
        if (typeof window !== 'undefined' && window.Tawk_API?.showWidget && !document.body.classList.contains('global-reel-platform-active')) {
          try { window.Tawk_API.showWidget(); } catch (e) {}
        }
      }
    }
  }, [open]);

  // Real-time search evaluation
  const searchResults = useMemo(() => {
    return searchNavigator(inputValue);
  }, [inputValue]);

  const handleNavigate = (path) => {
    if (path) {
      navigate(path);
      onClose();
      setInputValue('');
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (searchResults.topMatch) {
      handleNavigate(searchResults.topMatch.path);
    } else if (inputValue.trim()) {
      handleNavigate(`/tools/?q=${encodeURIComponent(inputValue.trim())}`);
    } else {
      handleNavigate('/tools/');
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1450 }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
            backdropFilter: 'blur(5px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 520, md: 560 },
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100dvh',
          bgcolor: 'var(--surface-1, #ffffff)',
          color: 'var(--text-primary, #0f172a)',
          borderLeft: '1px solid var(--border-color, #e2e8f0)',
          boxShadow: '-12px 0 36px rgba(0, 0, 0, 0.16)',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          overflow: 'hidden',
        },
      }}
    >
      {/* Clean Header Bar (Fixed at top) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: { xs: 2, sm: 2.75 },
          pb: 2,
          borderBottom: '1px solid var(--border-color, #e2e8f0)',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: 'rgba(37, 99, 235, 0.1)',
              color: '#2563eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaCompass size={20} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                color: 'var(--text-primary, #0f172a)',
                fontWeight: 700,
                fontSize: { xs: '1.05rem', sm: '1.2rem' },
                lineHeight: 1.25,
              }}
            >
              What brings you to Goafreet?
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'var(--text-secondary, #64748b)',
                fontSize: '0.78rem',
                mt: 0.25,
              }}
            >
              Search tools, services & resources or browse by category
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close Smart Guide"
          size="small"
          sx={{
            color: 'var(--text-secondary, #64748b)',
            bgcolor: 'var(--surface-2, #f8fafc)',
            border: '1px solid var(--border-color, #e2e8f0)',
            '&:hover': {
              color: 'var(--text-primary, #0f172a)',
              bgcolor: 'var(--surface-3, #f1f5f9)',
            },
          }}
        >
          <FaTimes size={15} />
        </IconButton>
      </Box>

      {/* Scrollable Content Container (Cards scroll smoothly inside here) */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: { xs: 2, sm: 2.75 },
          display: 'flex',
          flexDirection: 'column',
          WebkitOverflowScrolling: 'touch',
        }}
      >

      {/* 4 Mode Cards */}
      <Box
        sx={{
          mt: 2.5,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.25,
        }}
      >
        {OPTIONS.map((opt) => {
          const isActive = activeTab === opt.id;
          return (
            <Box
              key={opt.id}
              onClick={() => setActiveTab(opt.id)}
              sx={{
                bgcolor: isActive
                  ? 'rgba(37, 99, 235, 0.08)'
                  : 'var(--surface-2, #f8fafc)',
                border: `1.5px solid ${
                  isActive ? '#2563eb' : 'var(--border-color, #e2e8f0)'
                }`,
                borderRadius: '12px',
                p: 1.75,
                cursor: 'pointer',
                transition: 'all 0.18s ease',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                '&:hover': {
                  bgcolor: isActive
                    ? 'rgba(37, 99, 235, 0.1)'
                    : 'var(--surface-3, #f1f5f9)',
                  borderColor: isActive
                    ? '#2563eb'
                    : 'rgba(37, 99, 235, 0.35)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  color: isActive ? '#2563eb' : 'var(--text-secondary, #64748b)',
                  mb: 0.75,
                }}
              >
                {opt.icon}
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: 'var(--text-primary, #0f172a)',
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    mb: 0.25,
                    lineHeight: 1.25,
                  }}
                >
                  {opt.title}
                </Typography>
                <Typography
                  sx={{
                    color: 'var(--text-secondary, #64748b)',
                    fontSize: '0.72rem',
                    lineHeight: 1.35,
                  }}
                >
                  {opt.desc}
                </Typography>
              </Box>

              {isActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    color: '#2563eb',
                  }}
                >
                  <IoCheckmarkCircleOutline size={18} />
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Dynamic Input Field */}
      <Box sx={{ mt: 2.75 }}>
        <Typography
          sx={{
            color: 'var(--text-primary, #0f172a)',
            fontWeight: 700,
            fontSize: '0.86rem',
            mb: 0.75,
          }}
        >
          What are you trying to achieve?
        </Typography>

        <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'var(--surface-2, #f8fafc)',
              border: '1.5px solid var(--border-color, #e2e8f0)',
              borderRadius: '12px',
              px: 1.75,
              py: 0.5,
              transition: 'all 0.2s ease',
              '&:focus-within': {
                borderColor: '#2563eb',
                boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.15)',
                bgcolor: 'var(--surface-1, #ffffff)',
              },
            }}
          >
            <FaSearch
              size={15}
              style={{
                color: 'var(--text-secondary, #94a3b8)',
                marginRight: 10,
                flexShrink: 0,
              }}
            />
            <InputBase
              fullWidth
              autoFocus
              placeholder="Type keyword, service or tool (e.g. facebook, amazon, seo, logo)..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              sx={{
                color: 'var(--text-primary, #0f172a)',
                fontSize: '0.9rem',
                py: 1,
              }}
            />
            {inputValue && (
              <IconButton
                size="small"
                onClick={() => setInputValue('')}
                sx={{
                  p: 0.5,
                  color: 'var(--text-secondary, #94a3b8)',
                  '&:hover': { color: 'var(--text-primary, #0f172a)' },
                }}
              >
                <FaTimes size={13} />
              </IconButton>
            )}
          </Box>
        </form>
      </Box>

      {/* Dynamic Results & Category Options Area */}
      <Box sx={{ mt: 2.5, flex: 1, minHeight: 180 }}>
        {/* CASE 1: USER IS TYPING — SHOW LIVE MATCHING RESULTS */}
        {inputValue.trim().length > 0 ? (
          <Fade in={true} timeout={250}>
            <Box>
              {searchResults.hasResults ? (
                <>
                  {/* Category Pills Header */}
                  {searchResults.matchingCategoryNames.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'var(--text-secondary, #64748b)',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '0.6px',
                          fontSize: '0.68rem',
                          display: 'block',
                          mb: 0.75,
                        }}
                      >
                        Relevant Categories
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {searchResults.matchingCategoryNames.map((catName) => (
                          <Chip
                            key={catName}
                            label={catName}
                            size="small"
                            onClick={() => setInputValue(catName)}
                            sx={{
                              bgcolor: 'rgba(37, 99, 235, 0.08)',
                              color: '#2563eb',
                              fontWeight: 600,
                              fontSize: '0.74rem',
                              border: '1px solid rgba(37, 99, 235, 0.25)',
                              cursor: 'pointer',
                              '&:hover': {
                                bgcolor: 'rgba(37, 99, 235, 0.16)',
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Top Matching Items List */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-secondary, #64748b)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    Matching Tools, Services & Guides ({searchResults.allMatches.length})
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {searchResults.allMatches.map((item) => (
                      <Box
                        key={item.id}
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            bgcolor: 'var(--surface-3, #f1f5f9)',
                            borderColor: '#2563eb',
                            transform: 'translateX(3px)',
                            boxShadow: '0 2px 10px rgba(37, 99, 235, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                          <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>
                            {item.icon}
                          </Typography>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.88rem',
                                  color: 'var(--text-primary, #0f172a)',
                                  lineHeight: 1.3,
                                }}
                              >
                                {item.title}
                              </Typography>
                              {item.badge && (
                                <Chip
                                  label={item.badge}
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 700,
                                    bgcolor: `${item.badgeColor || '#2563eb'}18`,
                                    color: item.badgeColor || '#2563eb',
                                    border: `1px solid ${item.badgeColor || '#2563eb'}30`,
                                  }}
                                />
                              )}
                            </Box>
                            <Typography
                              sx={{
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.74rem',
                                lineHeight: 1.4,
                              }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            color: '#2563eb',
                            mt: 0.5,
                            flexShrink: 0,
                            opacity: 0.8,
                          }}
                        >
                          <FaArrowRight size={13} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              ) : (
                /* No exact match: Show suggestions gracefully (NEVER leave empty!) */
                <Box sx={{ textAlign: 'left', py: 1 }}>
                  <Typography
                    sx={{
                      color: '#dc2626',
                      fontWeight: 600,
                      fontSize: '0.82rem',
                      mb: 1.5,
                    }}
                  >
                    No exact match for "{inputValue}". Choose from relevant categories below:
                  </Typography>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1,
                    }}
                  >
                    {POPULAR_CATEGORIES.map((cat) => (
                      <Box
                        key={cat.id}
                        onClick={() => handleNavigate(cat.path)}
                        sx={{
                          p: 1.25,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            borderColor: cat.color,
                            bgcolor: 'var(--surface-3, #f1f5f9)',
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: '1.1rem' }}>{cat.icon}</Typography>
                        <Box>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.8rem',
                              color: 'var(--text-primary, #0f172a)',
                            }}
                          >
                            {cat.title}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.68rem',
                              color: 'var(--text-secondary, #64748b)',
                            }}
                          >
                            {cat.desc}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Fade>
        ) : (
          /* CASE 2: INPUT IS EMPTY — SHOW CONTEXTUAL CATEGORIES ACCORDING TO ACTIVE TAB */
          <Fade in={true} timeout={200}>
            <Box>
              {activeTab === 'find' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-secondary, #64748b)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 1.25,
                    }}
                  >
                    Explore Categories & Core Hubs
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.25,
                    }}
                  >
                    {POPULAR_CATEGORIES.map((cat) => (
                      <Box
                        key={cat.id}
                        onClick={() => handleNavigate(cat.path)}
                        sx={{
                          p: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          '&:hover': {
                            borderColor: cat.color,
                            bgcolor: 'var(--surface-3, #f1f5f9)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '1.2rem' }}>{cat.icon}</Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.84rem',
                              color: 'var(--text-primary, #0f172a)',
                            }}
                          >
                            {cat.title}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            color: 'var(--text-secondary, #64748b)',
                            fontSize: '0.72rem',
                            lineHeight: 1.35,
                          }}
                        >
                          {cat.desc}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {activeTab === 'solve' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-secondary, #64748b)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 1.25,
                    }}
                  >
                    Common Business Goals & Solutions (Tap to explore)
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {GOAL_PROMPTS.map((prompt) => (
                      <Box
                        key={prompt.label}
                        onClick={() => setInputValue(prompt.query)}
                        sx={{
                          p: 1.25,
                          px: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            bgcolor: 'rgba(37, 99, 235, 0.06)',
                            borderColor: '#2563eb',
                            transform: 'translateX(3px)',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            color: 'var(--text-primary, #0f172a)',
                          }}
                        >
                          {prompt.label}
                        </Typography>
                        <FaArrowRight size={12} color="#2563eb" />
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {activeTab === 'explore' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-secondary, #64748b)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 1.25,
                    }}
                  >
                    Popular Interactive Tools & Hubs
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      {
                        title: 'Global Innovation Explorer',
                        desc: 'Search verified technologies, open-source algorithms & patents.',
                        icon: '🌐',
                        path: '/innovation/',
                        badge: 'New',
                      },
                      {
                        title: 'Technology News Live',
                        desc: 'Real-time engineering announcements & release notes.',
                        icon: '📰',
                        path: '/technology-news/',
                        badge: 'Feed',
                      },
                      {
                        title: 'Zepto & Blinkit Margin Planners',
                        desc: 'Calculate dark store fees, customer returns & net settlement.',
                        icon: '⚡',
                        path: '/tools/quick-commerce/zepto-fee-calculator/',
                        badge: 'Calculator',
                      },
                      {
                        title: 'Reel Script Studio',
                        desc: 'Algorithm hook timing and dynamic short-form script generator.',
                        icon: '🎬',
                        path: '/tools/social-media/reel-script-studio/',
                        badge: 'Tool',
                      },
                    ].map((item) => (
                      <Box
                        key={item.title}
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          p: 1.25,
                          px: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            borderColor: '#2563eb',
                            bgcolor: 'var(--surface-3, #f1f5f9)',
                            transform: 'translateX(3px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Typography sx={{ fontSize: '1.15rem' }}>{item.icon}</Typography>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: '0.84rem',
                                  color: 'var(--text-primary, #0f172a)',
                                }}
                              >
                                {item.title}
                              </Typography>
                              <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  bgcolor: 'rgba(37, 99, 235, 0.12)',
                                  color: '#2563eb',
                                }}
                              />
                            </Box>
                            <Typography
                              sx={{
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.72rem',
                              }}
                            >
                              {item.desc}
                            </Typography>
                          </Box>
                        </Box>
                        <FaArrowRight size={12} color="#2563eb" />
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {activeTab === 'learn' && (
                <>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'var(--text-secondary, #64748b)',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      fontSize: '0.68rem',
                      display: 'block',
                      mb: 1.25,
                    }}
                  >
                    Research Guides & Strategic Knowledge
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      {
                        title: 'Meta & Facebook Platform Policy Feed',
                        desc: 'Historical intelligence on Meta advertising and business policies.',
                        icon: '📡',
                        path: '/resources/platform-updates/meta/',
                      },
                      {
                        title: 'Website UX Architecture Facts',
                        desc: 'Empirical data on web design conversion, speed & layout.',
                        icon: '📐',
                        path: '/facts-about-website-designing/',
                      },
                    ].map((item) => (
                      <Box
                        key={item.title}
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          p: 1.25,
                          px: 1.5,
                          borderRadius: '10px',
                          bgcolor: 'var(--surface-2, #f8fafc)',
                          border: '1px solid var(--border-color, #e2e8f0)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          transition: 'all 0.18s ease',
                          '&:hover': {
                            borderColor: '#2563eb',
                            bgcolor: 'var(--surface-3, #f1f5f9)',
                            transform: 'translateX(3px)',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Typography sx={{ fontSize: '1.15rem' }}>{item.icon}</Typography>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.84rem',
                                color: 'var(--text-primary, #0f172a)',
                              }}
                            >
                              {item.title}
                            </Typography>
                            <Typography
                              sx={{
                                color: 'var(--text-secondary, #64748b)',
                                fontSize: '0.72rem',
                              }}
                            >
                              {item.desc}
                            </Typography>
                          </Box>
                        </Box>
                        <FaArrowRight size={12} color="#2563eb" />
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          </Fade>
        )}
      </Box>
      </Box>

      {/* Docked Action Button Footer (Isolated with solid background, zero overlap) */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          pb: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          borderTop: '1px solid var(--border-color, #e2e8f0)',
          bgcolor: 'var(--surface-1, #ffffff)',
          flexShrink: 0,
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Button
          onClick={handleSubmit}
          variant="contained"
          disableElevation
          fullWidth
          endIcon={<FaArrowRight />}
          sx={{
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            color: '#ffffff',
            py: 1.35,
            borderRadius: '10px',
            fontSize: '0.92rem',
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: '0 4px 14px rgba(37, 99, 235, 0.3)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(37, 99, 235, 0.4)',
            },
          }}
        >
          {searchResults.topMatch
            ? `Go to ${searchResults.topMatch.title}`
            : inputValue.trim()
            ? `Search for "${inputValue.trim()}"`
            : 'Explore All Tools & Services'}
        </Button>
      </Box>
    </Drawer>
  );
};

export default SmartGuidePanel;
